import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';

// ── Stockage hors-ligne des mini-apps du compte utilisateur ─────────────
// Principe : la source de vérité immédiate est IndexedDB (fonctionne sans
// réseau). Chaque écriture est ensuite poussée vers le Backend dans
// `core_settings` (clé/valeur JSON) quand la connexion est disponible.
// Une écriture distante échouée est mise en file d'attente et rejouée au
// retour du réseau, sans jamais écraser la version locale.

const DB_NAME = 'zifek-user-apps';
const DB_VERSION = 1;
const STORE = 'kv';
const QUEUE_KEY = '__pending__';

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB indisponible'));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

async function withStore<T>(mode: 'readonly' | 'readwrite', run: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = run(tx.objectStore(STORE));
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error);
  });
}

export async function localGet<T>(key: string): Promise<T | null> {
  try {
    const value = await withStore<T | undefined>('readonly', (s) => s.get(key));
    return value === undefined ? null : (value as T);
  } catch {
    return null;
  }
}

export async function localSet(key: string, value: unknown): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.put(value, key));
  } catch {
    /* stockage local indisponible : on continue */
  }
}

export async function localDelete(key: string): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.delete(key));
  } catch {
    /* ignore */
  }
}

// ── Synchronisation Backend (core_settings) ────────────────────────────

export type StorageScope = 'user' | 'app' | 'platform';

interface RemoteRow {
  id: number;
  value: unknown;
}

function buildQuery(scope: StorageScope, scopeRef: string, key: string, userId?: number) {
  let query = supabase
    .from('core_settings')
    .select('id, value')
    .eq('level', scope)
    .eq('setting_key', key);
  query = scopeRef ? query.eq('scope_ref', scopeRef) : query.is('scope_ref', null);
  query = userId != null ? query.eq('user_id', userId) : query.is('user_id', null);
  return query.limit(1);
}

export async function remoteGet<T>(
  scope: StorageScope,
  scopeRef: string,
  key: string,
  userId?: number,
): Promise<T | null> {
  if (!navigator.onLine) return null;
  try {
    const { data, error } = await buildQuery(scope, scopeRef, key, userId);
    if (error) return null;
    const row = (data as RemoteRow[] | null)?.[0];
    return row ? (row.value as T) : null;
  } catch {
    return null;
  }
}

export async function remoteSet(
  scope: StorageScope,
  scopeRef: string,
  key: string,
  value: unknown,
  userId?: number,
): Promise<boolean> {
  if (!navigator.onLine) return false;
  try {
    const { data, error } = await buildQuery(scope, scopeRef, key, userId);
    if (error) return false;
    const row = (data as RemoteRow[] | null)?.[0];
    if (row) {
      const { error: updError } = await supabase
        .from('core_settings')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('id', row.id);
      return !updError;
    }
    const { error: insError } = await supabase.from('core_settings').insert({
      level: scope,
      scope_ref: scopeRef || null,
      user_id: userId ?? null,
      setting_key: key,
      value,
    });
    return !insError;
  } catch {
    return false;
  }
}

// ── File d'attente hors-ligne ──────────────────────────────────────────

interface PendingEntry {
  scope: StorageScope;
  scopeRef: string;
  key: string;
  userId?: number;
  value: unknown;
}

async function readQueue(): Promise<PendingEntry[]> {
  return (await localGet<PendingEntry[]>(QUEUE_KEY)) || [];
}

async function writeQueue(entries: PendingEntry[]): Promise<void> {
  await localSet(QUEUE_KEY, entries);
}

async function enqueue(entry: PendingEntry): Promise<void> {
  const queue = await readQueue();
  const next = queue.filter(
    (q) => !(q.scope === entry.scope && q.scopeRef === entry.scopeRef && q.key === entry.key && q.userId === entry.userId),
  );
  next.push(entry);
  await writeQueue(next);
}

let flushing = false;

export async function flushPendingSync(): Promise<number> {
  if (flushing || !navigator.onLine) return 0;
  flushing = true;
  let done = 0;
  try {
    const queue = await readQueue();
    if (queue.length === 0) return 0;
    const remaining: PendingEntry[] = [];
    for (const entry of queue) {
      const ok = await remoteSet(entry.scope, entry.scopeRef, entry.key, entry.value, entry.userId);
      if (ok) done += 1;
      else remaining.push(entry);
    }
    await writeQueue(remaining);
  } finally {
    flushing = false;
  }
  return done;
}

// ── Hook de données persistant (local d'abord, Backend ensuite) ────────

export interface AppDataState<T> {
  data: T;
  setData: (next: T | ((prev: T) => T)) => void;
  ready: boolean;
  syncing: boolean;
  offline: boolean;
  pending: number;
  syncError: string | null;
  forceSync: () => Promise<void>;
}

export function useAppData<T>(
  key: string,
  initial: T,
  options?: { scope?: StorageScope; scopeRef?: string; userId?: number },
): AppDataState<T> {
  const scope = options?.scope || 'user';
  const scopeRef = options?.scopeRef || '';
  const userId = options?.userId;
  const storageKey = `${scope}:${scopeRef}:${userId ?? 'anon'}:${key}`;

  const [data, setDataState] = useState<T>(initial);
  const [ready, setReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState(0);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const initialRef = useRef(initial);

  const refreshPending = useCallback(async () => {
    const queue = await readQueue();
    setPending(queue.length);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const bootstrap = async () => {
      const local = await localGet<T>(storageKey);
      if (!cancelled && local != null) setDataState(local);
      const remote = await remoteGet<T>(scope, scopeRef, key, userId);
      if (!cancelled && remote != null) {
        const localHasData = local != null;
        if (!localHasData) setDataState(remote);
        else {
          // la version locale est la plus fraîche (édits hors ligne)
          await enqueue({ scope, scopeRef, key, userId, value: local });
        }
      }
      if (!cancelled) {
        setReady(true);
        await refreshPending();
      }
    };
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [storageKey, scope, scopeRef, key, userId, refreshPending]);

  useEffect(() => {
    const onOnline = async () => {
      setOffline(false);
      setSyncing(true);
      await flushPendingSync();
      await refreshPending();
      setSyncing(false);
    };
    const onOffline = () => setOffline(true);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [refreshPending]);

  const persistRemote = useCallback(
    async (value: T) => {
      if (!navigator.onLine) {
        await enqueue({ scope, scopeRef, key, userId, value });
        await refreshPending();
        return;
      }
      setSyncing(true);
      setSyncError(null);
      const ok = await remoteSet(scope, scopeRef, key, value, userId);
      if (!ok) {
        await enqueue({ scope, scopeRef, key, userId, value });
        setSyncError('Synchronisation en attente — vos données sont conservées sur cet appareil.');
      }
      await refreshPending();
      setSyncing(false);
    },
    [scope, scopeRef, key, userId, refreshPending],
  );

  const setData = useCallback(
    (next: T | ((prev: T) => T)) => {
      setDataState((prev) => {
        const value = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        void localSet(storageKey, value);
        void persistRemote(value);
        return value;
      });
    },
    [storageKey, persistRemote],
  );

  const forceSync = useCallback(async () => {
    setSyncing(true);
    setSyncError(null);
    await localSet(storageKey, data);
    const ok = await remoteSet(scope, scopeRef, key, data, userId);
    if (!ok) setSyncError('Impossible de synchroniser pour le moment.');
    await flushPendingSync();
    await refreshPending();
    setSyncing(false);
  }, [data, storageKey, scope, scopeRef, key, userId, refreshPending]);

  return {
    data,
    setData,
    ready,
    syncing,
    offline,
    pending,
    syncError,
    forceSync,
  };
}

/** Identifiant court et stable pour les créations locales. */
export function localId(prefix = 'id'): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function formatShortDate(iso: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(iso: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function downloadTextFile(filename: string, content: string, mime = 'text/plain;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}